<template>
  <Modal
    size="md"
    @close="$emit('close')"
  >
    <template #header>
      Manage Categories
    </template>

    <template #body>
      <div class="space-y-4">
        <form
          class="flex items-end gap-3"
          @submit.prevent="createCategory"
        >
          <label class="flex-1">
            <span class="text-sm font-medium text-gray-700">New category</span>

            <AppInput
              v-model="createForm.form.description"
              name="description"
              type="text"
              class="form-input mt-1"
              :error="createForm.getErrors('description')"
              placeholder="Category description"
            />
          </label>

          <button
            type="submit"
            class="btn btn--primary-blue"
            :disabled="storeAction.is(States.LOADING) || !createForm.form.description.trim()"
          >
            Add
          </button>
        </form>

        <div
          v-if="indexAction.is(States.LOADING)"
          class="flex justify-center py-8"
        >
          <AppSpinner />
        </div>

        <div
          v-else-if="!categories.length"
          class="p-6 text-center text-gray-500"
        >
          No categories found.
        </div>

        <Sortable
          v-else
          :list="categories"
          item-key="id"
          tag="ul"
          class="divide-y divide-gray-200 rounded-lg border border-gray-200"
          :class="{ 'opacity-50 pointer-events-none': sortAction.is(States.LOADING) }"
          :options="{
            handle: '.variation-order-category-drag-handle'
          }"
          @end="onSortEnd"
        >
          <template #item="{ element: category }">
            <li
              :data-variation-order-category-id="category.id"
              class="flex items-center gap-3 bg-white p-3"
            >
              <div
                class="variation-order-category-drag-handle cursor-grab active:cursor-grabbing rounded-md p-1 hover:bg-gray-100"
                title="Drag to reorder"
              >
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-4 w-4 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 8h16M4 16h16"
    />
</svg>
              </div>

              <template v-if="editingId === category.id">
                <AppInput
                  v-model="editDescription"
                  name="edit_description"
                  type="text"
                  class="form-input h-8 flex-1"
                  @keydown.enter.prevent="saveEdit(category)"
                  @keydown.escape.prevent="cancelEdit"
                />

                <button
                  type="button"
                  class="btn-icon btn-icon-green"
                  title="Save"
                  @click="saveEdit(category)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
                </button>

                <button
                  type="button"
                  class="btn-icon btn-icon-red"
                  title="Cancel"
                  @click="cancelEdit"
                >
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="h-4 w-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
                </button>
              </template>

              <template v-else>
                <span class="min-w-0 flex-1 truncate text-sm text-gray-800">
                  {{ category.description }}
                </span>

                <button
                  type="button"
                  class="btn-icon btn-icon-blue"
                  title="Edit"
                  @click="startEdit(category)"
                >
                  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-4 w-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
                </button>

                <button
                  type="button"
                  class="btn-icon btn-icon-red"
                  title="Delete"
                  @click="deleteCategory(category)"
                >
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="h-4 w-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                </button>
              </template>
            </li>
          </template>
        </Sortable>
      </div>
    </template>

    <template #footer>
      <div class="flex w-full justify-end">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          Close
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import VariationOrderCategory from '@/models/VariationOrderCategory';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { Sortable } from 'sortablejs-vue3';
import Swal from 'sweetalert2';
import { computed, onMounted, ref } from 'vue';

const props = defineProps<{
  projectId: Id;
}>();

defineEmits<{
  (e: 'close'): void;
}>();

const categoriesStore = useVariationOrderCategoriesStore();
const indexAction = useStoreApiAction(categoriesStore.indexProject);
const storeAction = useStoreApiAction(categoriesStore.storeProject);
const updateAction = useStoreApiAction(categoriesStore.update);
const destroyAction = useStoreApiAction(categoriesStore.destroy);
const sortAction = useStoreApiAction(categoriesStore.sort);

const createForm = useFormData({
  description: '',
});

const editingId = ref<number | undefined>(undefined);
const editDescription = ref('');

const categories = computed(() => {
  return categoriesStore.models
    .where('project_id', Number(props.projectId))
    .orderBy('sort_order')
    .whereNull('package_id')
    .get();
});

onMounted(() => {
  indexAction.request(props.projectId).catch(console.error);
});

const createCategory = () => {
  createForm.resetErrors();

  storeAction.request(props.projectId, createForm.form).then(() => {
    createForm.resetData();
  }).catch((error) => {
    createForm.setErrorsFromAxiosError(error);
  });
};

const startEdit = (category: VariationOrderCategory) => {
  editingId.value = category.id;
  editDescription.value = category.description;
};

const cancelEdit = () => {
  editingId.value = undefined;
  editDescription.value = '';
};

const saveEdit = (category: VariationOrderCategory) => {
  const description = editDescription.value.trim();

  if(!description) {
    return;
  }

  updateAction.request(category.id, { description }).then(() => {
    cancelEdit();
  }).catch(console.error);
};

const deleteCategory = (category: VariationOrderCategory) => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete this category?',
    text: 'Variation orders in this category will become uncategorized.',
    showConfirmButton: true,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      destroyAction.request(category.id);
    }
  });
};

const onSortEnd = (e: { item: HTMLElement; newIndex: number }) => {
  const categoryId = e.item.dataset.variationOrderCategoryId;

  if(!categoryId) {
    return;
  }

  sortAction.request(categoryId, {
    sort_order: e.newIndex + 1,
  }).catch(console.error);
};
</script>
